import React, { useRef, ChangeEvent, forwardRef, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useFormContext } from "react-hook-form"; 

interface ImageUploadProps {
  name: string;
  image?: string;
}

export const ImageUpload = forwardRef<HTMLInputElement, ImageUploadProps>(
  ({ name, image }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [preview, setPreview] = React.useState<string>("");
    const { register, setValue, watch } = useFormContext();

    const currentFile = watch(name);

    useEffect(() => {
      // 초기 이미지가 있을 경우 미리보기 설정
      if (image) {
        setPreview(image);
      }
    }, [image]);

    useEffect(() => {
      // 등록된 파일이 있을 경우 미리보기 설정
      if (currentFile instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(currentFile);
      }
    }, [currentFile, name]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setValue(name, file, { shouldValidate: true });
      }
    };

    const handleDelete = () => {
      setPreview("");
      setValue(name, null, { shouldValidate: true });
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    return (
      <Box>
        <input
          type="file"
          accept="image/*"
          {...register(name)}
          onChange={handleFileChange}
          ref={(e) => {
            if (e) {
              register(name).ref(e);
              inputRef.current = e;
              if (typeof ref === "function") ref(e);
              else if (ref) ref.current = e;
            }
          }}
          style={{ display: "none" }}
        />

        {!preview ? (
          <Box
            onClick={() => inputRef.current?.click()}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 1,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": {
                borderColor: "primary.main",
              },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary" }} />
            <Typography color="text.secondary" mt={1}>
              Click to upload image
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: "relative" }}>
            <img
              src={preview}
              alt="Preview"
              style={{
                width: "100%",
                maxHeight: "300px",
                objectFit: "contain",
              }}
            />
            <IconButton
              onClick={handleDelete}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.5)",
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.7)",
                },
              }}
            >
              <DeleteIcon sx={{ color: "white" }} />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  }
);

ImageUpload.displayName = "ImageUpload";

export default ImageUpload;
